import { AuthState } from '@aws-amplify/ui-components';
import { routerActions } from 'connected-react-router';
import {
  Box,
  Container,
  Flex,
  Icon,
  Link,
  Menu,
  MenuItem,
  MenuButton,
  MenuList,
  Button,
  Text,
  Collapse,
} from '@chakra-ui/react';
import React, { memo, useEffect, useState } from 'react';
import {
  FiCalendar,
  FiFeather,
  FiUser,
  FiFileText,
  FiHome,
  FiDollarSign,
  FiLogOut,
} from 'react-icons/fi';
import { TiArrowSortedDown } from 'react-icons/ti';
import { useAppDispatch, useAppSelector } from '../hooks';
import { signOutAsync } from '../slice/userSlice';
import { startLoading, stopLoading } from '../slice/loadingSlice';
import './shell.scss';
import { Auth } from 'aws-amplify';
import { ASSETS_IMG } from '../utils/assets';

const NavItem = ({ icon, title, active, onClick, children, pathname }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = Array.isArray(children) && children.length > 0;

  return (
    <Flex mt={3} flexDir="column" w="100%">
      <Link
        backgroundColor={active ? '#cbe7ff' : 'inherit'}
        p={3}
        borderRadius={2}
        _hover={{ textDecor: 'none', backgroundColor: '#e8f4ff' }}
        onClick={() => {
          if (hasChildren) {
            setIsOpen(!isOpen);
          } else {
            onClick();
          }
        }}
      >
        <Flex align="center" justify="space-between">
          <Flex align="center">
            {icon && <Icon as={icon} fontSize="xl" color={active ? '#82AAAD' : 'gray.500'} />}
            <Text ml={5}>{title}</Text>
          </Flex>
          {hasChildren && (
            <TiArrowSortedDown
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </Flex>
      </Link>

      {/* Sub-navigation */}
      {hasChildren && (
        <Collapse
          in={isOpen}
          transition={{
            enter: { duration: 0.2, ease: 'easeOut' },
            exit: { duration: 0.2, ease: 'easeIn' },
          }}
          animateOpacity
        >
          <Box ml={8} mt={1}>
            {children.map((child: any) => (
              <Link
                key={child.title}
                display="block"
                p={2}
                borderRadius={2}
                backgroundColor={child.pathname === pathname ? '#dff1ff' : 'inherit'}
                _hover={{ backgroundColor: '#f0faff' }}
                onClick={() => onClick(child.pathname)}
              >
                {child.title}
              </Link>
            ))}
          </Box>
        </Collapse>
      )}
    </Flex>
  );
};

const navs = [
  {
    icon: FiHome,
    title: '企業',
    active: false,
    pathname: '#',
    children: [
      {
        title: '企業一覧',
        pathname: '/companies',
      },
      {
        title: '契約プラン一覧',
        pathname: '/agreements',
      },
    ],
  },
  {
    icon: FiUser,
    title: '学習者',
    active: false,
    pathname: '#',
    children: [
      {
        title: '学習者一覧',
        pathname: '/learners',
      },
    ],
  },
  { icon: FiFeather, title: '教師', active: false, pathname: '/teachers' },
  {
    icon: FiCalendar,
    title: 'レッスン',
    active: false,
    pathname: '#',
    children: [
      {
        title: 'レッスン一覧',
        pathname: '/lessons',
      },
      {
        title: 'フィードバック一覧',
        pathname: '/feedbacks',
      },
      /* {
        title: '申し送り一覧',
        pathname: '/lesson_notes',
      }, */
    ],
  },
  {
    icon: FiFileText,
    title: '運用',
    active: false,
    pathname: '#',
    children: [
      {
        title: 'お知らせ',
        pathname: '/notices',
      },
      {
        title: 'メンテナンス',
        pathname: '/maintenance',
      },
    ],
  },
  { icon: FiDollarSign, title: '教師請求', active: false, pathname: '/payments_teacher' },
];
export interface IShellProps {
  children: React.ReactNode;
}

export var orgKey = React.createContext('kokoka');
export var currentUserId = React.createContext('');

const getOrgKeyOrCurrentUserId = async (attributeName: string = 'currentUserId') => {
  const authenticatedUser = await Auth.currentAuthenticatedUser().catch((e) => null);
  if (authenticatedUser) {
    const orgKey = await authenticatedUser.attributes['custom:companyId'];
    const currentUserId = await authenticatedUser.attributes['sub'];
    return attributeName === 'orgKey' ? orgKey : currentUserId;
  }
  return '';
};

export const Shell: React.NamedExoticComponent<IShellProps> = memo(({ children }) => {
  const dispatch = useAppDispatch();
  const historyState = useAppSelector((state) => state.router);
  const userState = useAppSelector((state) => state.user);
  const [companyId, setCompanyId] = useState<any>('くらしスタディ はなすプラス');
  const onClickBrand = () => dispatch(routerActions.push({ pathname: '/top' }));

  // サインアウトはトークンの失効で通信が発生して数秒かかるため、その間は画面全体を読み込み中にして操作できないようにする
  const onClickSignout = async () => {
    dispatch(startLoading());
    try {
      await dispatch(signOutAsync());
    } finally {
      dispatch(stopLoading());
    }
  };

  useEffect(() => {
    getOrgKeyOrCurrentUserId().then((teacherId) => {
      currentUserId = React.createContext(teacherId);
    });
    return;
  });

  return (
    <>
      <Box
        display="flex"
        height="60px"
        borderBottom="1px solid #ddd"
        justifyContent="space-between"
        boxShadow={'sm'}
        alignItems="center"
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex={500}
        bg="white"
      >
        <Box alignItems="center" display="flex" flexWrap="wrap">
          <Box className="Logo" onClick={onClickBrand} style={{ cursor: 'pointer' }}>
            <img src={ASSETS_IMG.logo01} alt={companyId} />
          </Box>
          <br />
          <sub style={{ color: '#3f83bf', marginLeft: '5px', bottom: 'unset' }}>
            {companyId !== undefined && companyId.length > 0 ? ' | ' : ''} 運用管理システム
          </sub>
        </Box>
        <Box hidden={!(userState.authState === AuthState.SignedIn && userState.user)}>
          <Menu>
            <MenuButton
              as={Button}
              fontSize="12px"
              fontWeight={500}
              marginRight={3}
              height={'32px'}
              background={'white'}
              border="1px solid"
              borderColor="gray.300"
              borderRadius="4px"
              padding="1px 12px"
              rightIcon={<TiArrowSortedDown size={13} />}
              _hover={{ background: 'white', borderColor: 'gray.500' }}
              _focus={{ background: 'white', boxShadow: 'none' }}
              _active={{ background: 'white' }}
            >
              {userState.user?.email}
            </MenuButton>
            <MenuList>
              <MenuItem
                as={Button}
                fontSize="14px"
                fontWeight={400}
                leftIcon={<Icon as={FiLogOut} boxSize="22px" color="red.500" fontWeight="bold" />}
                background={'white'}
                onClick={onClickSignout}
                color="red.500"
                justifyContent="flex-start"
                ml={'7px'}
                _hover={{ background: 'transparent', color: 'red.500' }}
                _focus={{ background: 'transparent', boxShadow: 'none' }}
                _active={{ background: 'transparent' }}
              >
                Signout
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>
      </Box>
      <Flex>
        <Flex
          pos="sticky"
          borderRight="1px solid #ddd"
          boxShadow="sm"
          minW="200px"
          flexDir="column"
          justifyContent="space-between"
          top="0"
          h="100vh"
          overflowY="auto"
          hidden={!(userState.authState === AuthState.SignedIn && userState.user)}
        >
          <Flex className="scrollable-sidebar" flexDir="column" marginTop={'60px'} as="nav">
            {navs.map((nav) => (
              <NavItem
                key={nav.title}
                icon={nav.icon}
                title={nav.title}
                active={nav.pathname === historyState.location.pathname}
                pathname={historyState.location.pathname}
                onClick={(pathname?: string) => {
                  dispatch(routerActions.push(pathname || nav.pathname));
                }}
                children={nav.children}
              />
            ))}
          </Flex>
        </Flex>
        <Container
          className="Shell-children main-content"
          marginTop={'75px'}
          maxWidth={'100vw'}
          marginX={'2em'}
        >
          {children}
        </Container>
      </Flex>
    </>
  );
});
